import { useEffect, useState } from "react";
import { api } from "../api/client";
import { TagEditor } from "./TagEditor";
import type { UserTag, TagVocabulary } from "../api/types";

// Wraps TagEditor with the load-vocabulary + persist plumbing so the SAME tagging
// control (spec-4 dec-2..6) can be dropped onto the Users table, the org detail
// and the user detail. Add/remove go through /api/tags and update the chips
// optimistically; the owner: options come from the operators (dec-4). Defensive
// about missing inputs so it can be embedded in any page (tags/vocab default to
// empty) without coupling that page's tests to this control.
export function EntityTagSection({
  entityType,
  entityId,
  initialTags,
}: {
  entityType: "user" | "org";
  entityId: string;
  initialTags: UserTag[] | undefined;
}) {
  const [tags, setTags] = useState<UserTag[]>(initialTags ?? []);
  const [vocab, setVocab] = useState<TagVocabulary>({ vocabulary: [], ownerOptions: [] });
  const [busy, setBusy] = useState(false);

  useEffect(() => setTags(initialTags ?? []), [initialTags]);
  useEffect(() => {
    let cancelled = false;
    api<TagVocabulary>("/api/tags")
      .then((v) => {
        if (!cancelled && v) setVocab({ vocabulary: v.vocabulary ?? [], ownerOptions: v.ownerOptions ?? [] });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const add = async (scope: string, value: string) => {
    setBusy(true);
    try {
      await api("/api/tags", {
        method: "POST",
        body: JSON.stringify({ entityType, entityId, scope, value }),
      });
      setTags((t) =>
        t.some((x) => x.scope === scope && x.value === value)
          ? t
          : [...t, { scope, value, taggedBy: "", taggedByKind: "operator" }],
      );
    } finally {
      setBusy(false);
    }
  };

  const remove = async (scope: string, value: string) => {
    setBusy(true);
    try {
      await api("/api/tags", {
        method: "DELETE",
        body: JSON.stringify({ entityType, entityId, scope, value }),
      });
      setTags((t) => t.filter((x) => !(x.scope === scope && x.value === value)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <TagEditor
      tags={tags}
      vocabulary={vocab.vocabulary}
      ownerOptions={vocab.ownerOptions}
      busy={busy}
      onAdd={add}
      onRemove={remove}
    />
  );
}
